import { Button, type ButtonProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';

interface ContactPrimaryButtonProps extends ButtonProps {
  children: ReactNode;
}

/**
 * Primary (charcoal) button used across the contact drawer views. Thin wrapper
 * over the shared Button recipe with `colorPalette="primary"`: the solid variant
 * resolves to bg primary.900 (#1a1a1a -> dark) / color softCream / hover
 * primary.700 (#322F2A) — byte-identical to the prior hand-built values, so the
 * three call sites render unchanged. Only the drawer-specific bits stay local:
 * the 13px label, the centered icon+label layout, the heading font and the
 * custom focus ring. No shadow.
 *
 * Forwards its ref so a Tooltip trigger can anchor to it (used when the CTA is
 * disabled for lack of an email).
 */
const ContactPrimaryButton = forwardRef<HTMLButtonElement, ContactPrimaryButtonProps>(
  function ContactPrimaryButton({ children, ...rest }, ref) {
    return (
      <Button
        ref={ref}
        colorPalette="primary"
        type="button"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="1.5"
        fontFamily="heading"
        fontSize="13px"
        cursor="pointer"
        _focusVisible={{ outline: '2px solid', outlineColor: 'primary.900', outlineOffset: '2px' }}
        {...rest}
      >
        {children}
      </Button>
    );
  }
);

export default ContactPrimaryButton;
